import React from 'react';
import styles from './CustomItemsInsights.module.scss';
import { PriceFormat, Spinner } from 'components/UI';
import type { CustomItemsInsightsReport } from 'model/CustomItemsInsightsReport';
import { Tooltip } from 'components/UI/V3/Tooltip/Tooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';

interface Props {
    loading: boolean;
    report: CustomItemsInsightsReport;
}

const CustomItemsTable: React.FC<Props> = ({ loading, report }) => {
    const rows = report?.customItems ?? [];

    return (
        <div className={styles.tableWrapper}>
            <div className={styles.tableHeaderRow}>
                <div>Custom Item</div>
                <div>Category</div>
                <div className={styles.right}>Quantity</div>
                <div className={styles.right}>Avg Price</div>
                <div className={styles.right}>Gross Sales</div>
                <div className={styles.right}>Discounts</div>
                <div className={styles.right}>
                    Refunds
                    <Tooltip
                        content="Refunds are proportionally allocated from order-level refunds"
                        placement="top"
                        className={styles.customTooltip}
                    >
                        <div>
                            <FontAwesomeIcon
                                icon={faInfoCircle}
                                className={styles.tooltipIcon}
                            />
                        </div>
                    </Tooltip>
                </div>
                <div className={styles.right}>Net Sales</div>
                <div className={styles.right}>% of Net Custom Items Sales</div>
                <div className={styles.right}>% of All Net Sales</div>
            </div>

            {loading ? (
                <div className={styles.loadingRow}>
                    <Spinner className={styles.spinner} />
                </div>
            ) : (
                rows.map((r, idx) => (
                    <div
                        key={(r.customItemKey ?? r.customItemName ?? 'row') + idx}
                        className={styles.customItemRow}
                    >
                        <div>{r.customItemName ?? '—'}</div>
                        <div>{r.category ?? '—'}</div>

                        <div className={styles.right}>{r.quantity ?? 0}</div>

                        <div className={styles.right}>
                            <PriceFormat price={Number(r.avgPrice ?? 0).toFixed(2)} />
                        </div>

                        <div className={styles.right}>
                            <PriceFormat price={Number(r.grossSales ?? 0).toFixed(2)} />
                        </div>

                        <div className={styles.right}>
                            <PriceFormat price={Number(r.discounts ?? 0).toFixed(2)} />
                        </div>

                        <div className={styles.right}>
                            <PriceFormat price={Number(r.refunds ?? 0).toFixed(2)} />
                        </div>

                        <div className={styles.right}>
                            <PriceFormat price={Number(r.netSales ?? 0).toFixed(2)} />
                        </div>

                        <div className={styles.right}>{(Number(r.netCustomItemsSalesPct ?? 0) * 100).toFixed(2)}%</div>

                        <div className={styles.right}>{(Number(r.allNetSalesPct ?? 0) * 100).toFixed(2)}%</div>
                    </div>
                ))
            )}
        </div>
    );
};

export default CustomItemsTable;
